import React, { useState, useRef, useEffect } from "react";
import { IoSearch } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import { MdOutlineLogout, MdOutlinePayments, MdOutlineSettings } from "react-icons/md";
import { RiNotification2Line } from "react-icons/ri";
import { CgProfile } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SearchBar from "./E_SearchBar";
import { useUser } from "../../context/UserContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns up to 2 uppercase initials from a display name or email. */
function getInitials(user) {
  if (!user) return "?";
  const name = user.name || user.email || "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Avatar circle — colour derived from initials so it's stable per user. */
function Avatar({ user, size = 34 }) {
  const palettes = [
    { bg: "#DBEAFE", text: "#1E40AF" }, // blue
    { bg: "#D1FAE5", text: "#065F46" }, // green
    { bg: "#EDE9FE", text: "#5B21B6" }, // purple
    { bg: "#FEF3C7", text: "#92400E" }, // amber
    { bg: "#FCE7F3", text: "#9D174D" }, // pink
    { bg: "#E0F2FE", text: "#0369A1" }, // sky
  ];
  const initials = getInitials(user);
  const idx = initials.charCodeAt(0) % palettes.length;
  const { bg, text } = palettes[idx];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: text,
        fontSize: size * 0.38,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        userSelect: "none",
      }}
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notification item colours (avatar bg/text by category)
// ---------------------------------------------------------------------------
const NOTIF_COLORS = {
  approval:  { bg: "#D1FAE5", text: "#065F46" },
  payroll:   { bg: "#FEF3C7", text: "#92400E" },
  hr:        { bg: "#EDE9FE", text: "#5B21B6" },
  comment:   { bg: "#F1F5F9", text: "#475569" },
  default:   { bg: "#E0F2FE", text: "#0369A1" },
};

// ---------------------------------------------------------------------------
// Dropdown animation variants
// ---------------------------------------------------------------------------
const dropdownVariants = {
  hidden:  { opacity: 0, y: -6, scale: 0.97 },
  visible: { opacity: 1, y: 0,  scale: 1,   transition: { duration: 0.15, ease: "easeOut" } },
  exit:    { opacity: 0, y: -6, scale: 0.97, transition: { duration: 0.1 } },
};

// ---------------------------------------------------------------------------
// NotificationDropdown
// ---------------------------------------------------------------------------
function NotificationDropdown({ notifications, onMarkAllRead }) {
  const [tab, setTab] = useState("all");

  const unread = notifications.filter((n) => !n.read);
  const visible = tab === "unread" ? unread : notifications;

  return (
    <div
      style={{
        width: 320,
        background: "white",
        borderRadius: 14,
        border: "1px solid #E2E8F0",
        boxShadow: "0 8px 30px rgba(0,0,0,0.10)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px 0",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>
          Notifications
        </span>
        {unread.length > 0 && (
          <button
            onClick={onMarkAllRead}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              color: "#3B82F6",
              padding: 0,
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          padding: "10px 16px 0",
          borderBottom: "1px solid #F1F5F9",
          gap: 4,
        }}
      >
        {["all", "unread"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              padding: "4px 10px 9px",
              color: tab === t ? "#3B82F6" : "#64748B",
              fontWeight: tab === t ? 600 : 400,
              borderBottom: tab === t ? "2px solid #3B82F6" : "2px solid transparent",
              marginBottom: -1,
              textTransform: "capitalize",
            }}
          >
            {t}
            {t === "unread" && unread.length > 0 && (
              <span
                style={{
                  marginLeft: 5,
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  borderRadius: 9,
                  padding: "1px 6px",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {unread.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {visible.length === 0 ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              color: "#94A3B8",
              fontSize: 13,
            }}
          >
            {tab === "unread" ? "You're all caught up!" : "No notifications yet"}
          </div>
        ) : (
          visible.map((n) => {
            const color = NOTIF_COLORS[n.category] || NOTIF_COLORS.default;
            return (
              <div
                key={n.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                  background: n.read ? "transparent" : "#F0F7FF",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = n.read ? "#F8FAFC" : "#E8F2FF")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = n.read ? "transparent" : "#F0F7FF")
                }
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: color.bg,
                    color: color.text,
                    fontSize: 12,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {n.avatarLabel}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: "#1E293B", lineHeight: 1.45, margin: 0 }}>
                    <span style={{ fontWeight: 600 }}>{n.actor}</span> {n.message}
                  </p>
                  <p style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>{n.time}</p>
                </div>

                {/* Unread dot */}
                {!n.read && (
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#3B82F6",
                      flexShrink: 0,
                      marginTop: 5,
                    }}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div
          style={{
            borderTop: "1px solid #F1F5F9",
            padding: "10px 16px",
            textAlign: "center",
          }}
        >
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "#3B82F6",
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileDropdown
// ---------------------------------------------------------------------------
function ProfileDropdown({ user, onNavigateProfile, onLogout }) {
  const menuItems = [
    {
      label: "My profile",
      icon: <CgProfile size={16} />,
      onClick: onNavigateProfile,
      extra: (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#22C55E",
            display: "inline-block",
            marginLeft: "auto",
          }}
        />
      ),
    },
    
  ];

  return (
    <div
      style={{
        width: 220,
        background: "white",
        borderRadius: 14,
        border: "1px solid #E2E8F0",
        boxShadow: "0 8px 30px rgba(0,0,0,0.10)",
        overflow: "hidden",
      }}
    >
      {/* User info */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 16px",
        }}
      >
        <Avatar user={user} size={40} />
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: "#0F172A",
              margin: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.name || "Employee"}
          </p>
          <p
            style={{
              fontSize: 12,
              color: "#64748B",
              margin: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.email || ""}
          </p>
        </div>
      </div>

      <div style={{ height: 1, background: "#F1F5F9" }} />

      {/* Menu items */}
      <div style={{ padding: "6px 0" }}>
        {menuItems.map((item) => (
          <button
            key={item.label}
            onClick={item.onClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "9px 16px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "#1E293B",
              textAlign: "left",
              transition: "background 0.12s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <span style={{ color: "#64748B", display: "flex" }}>{item.icon}</span>
            {item.label}
            {item.extra}
          </button>
        ))}

        <div style={{ height: 1, background: "#F1F5F9", margin: "4px 0" }} />

        <button
          onClick={onLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "9px 16px",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            color: "#EF4444",
            textAlign: "left",
            transition: "background 0.12s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFF5F5")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
        >
          <MdOutlineLogout size={16} />
          Log out
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavBar
// ---------------------------------------------------------------------------

const SAMPLE_NOTIFICATIONS = [
  
];

function NavBar() {
  const navigate = useNavigate();
  const { currentUser, loading, setCurrentUser } = useUser();

  const [showNotification, setShowNotification] = useState(false);
  const [showProfile, setShowProfile]           = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [notifications, setNotifications]       = useState(SAMPLE_NOTIFICATIONS);

  const notifRef   = useRef();
  const profileRef = useRef();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleLogout = () => {
    localStorage.removeItem("token");
    setCurrentUser(null);
    navigate("/login");
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current   && !notifRef.current.contains(e.target))   setShowNotification(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock body scroll while mobile search is open
  useEffect(() => {
    document.body.style.overflow = showMobileSearch ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [showMobileSearch]);

  // Escape key closes mobile search
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setShowMobileSearch(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (loading) {
    return (
      <div className="h-16 flex items-center justify-between px-6">
        <motion.img src="/companylogo.png" alt="Company" className="h-10 w-auto" />
        <div className="flex items-center gap-6">
          <div className="h-8 w-8 rounded-full animate-pulse bg-slate-200" />
          <div className="h-8 w-8 rounded-full animate-pulse bg-slate-200" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-16 flex items-center justify-between px-6 relative">
        {/* Logo */}
        <motion.img
          src="/companylogo.png"
          alt="Company"
          className="h-10 w-auto"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        />

        <div className="flex items-center gap-5 relative">
          {/* Desktop search */}
          <div className="hidden sm:block">
            <SearchBar />
          </div>

          {/* Mobile search icon */}
          <button
            onClick={() => setShowMobileSearch(true)}
            aria-label="Open search"
            className="sm:hidden text-gray-500 hover:text-indigo-600 transition"
          >
            <IoSearch className="text-2xl" />
          </button>

          {/* ── Notifications ── */}
          <div ref={notifRef} style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowNotification((v) => !v);
                setShowProfile(false);
              }}
              aria-label="Toggle notifications"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748B",
                padding: 6,
                borderRadius: 8,
                position: "relative",
                display: "flex",
                alignItems: "center",
                transition: "color 0.15s",
              }}
            >
              <RiNotification2Line size={22} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#EF4444",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>

            <AnimatePresence>
              {showNotification && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 10px)",
                    zIndex: 50,
                  }}
                >
                  <NotificationDropdown
                    notifications={notifications}
                    onMarkAllRead={handleMarkAllRead}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Profile ── */}
          <div ref={profileRef} style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowProfile((v) => !v);
                setShowNotification(false);
              }}
              aria-label="Toggle profile menu"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                borderRadius: "50%",
              }}
            >
              <Avatar user={currentUser} size={34} />
            </button>

            <AnimatePresence>
              {showProfile && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 10px)",
                    zIndex: 50,
                  }}
                >
                  <ProfileDropdown
                    user={currentUser}
                    onNavigateProfile={() => {
                      setShowProfile(false);
                      navigate("/employee/profile");
                    }}
                    onLogout={handleLogout}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── Mobile Search Overlay ── */}
      <AnimatePresence>
        {showMobileSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 sm:hidden"
            onClick={() => setShowMobileSearch(false)}
          >
            <motion.div
              initial={{ y: -80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -80, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full p-4 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setShowMobileSearch(false)}
                  aria-label="Close search"
                  className="text-gray-500 hover:text-red-500 transition"
                >
                  <IoMdClose size={24} />
                </button>
                <h2 className="text-base font-semibold text-gray-800">Search</h2>
              </div>
              <SearchBar onNavigate={() => setShowMobileSearch(false)} isMobile />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default NavBar;